import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const Footer = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();

  return (
    <footer 
      className={`py-8 border-t text-center ${
        isDark 
          ? 'bg-slate-900 border-slate-800' 
          : 'bg-[#fff1f7] border-[#f2bfd4]'
      }`}
      role="contentinfo"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <div className="flex items-center justify-center gap-3 mb-3">
          <img src="/LorenAura.png" alt="Loren Aura" className="h-12 w-12" width="48" height="48" decoding="async" />
          <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
            {t.footer.brand}
          </h3>
        </div>
        
        <p className={`text-base mb-5 max-w-md mx-auto leading-relaxed ${
          isDark ? 'text-slate-400' : 'text-[#4b3a2a]'
        }`}>
          {t.footer.tagline}
        </p>
        
        {/* Contact Links - 44px touch targets */}
        <nav 
          className={`flex flex-wrap justify-center gap-4 mb-5 ${
            isDark ? 'text-slate-400' : 'text-[#4b3a2a]'
          }`}
          aria-label="Contact information"
        >
          <a 
            href={`tel:${t.whatsapp.number}`}
            className="flex items-center gap-2 min-h-[44px] px-4 rounded-lg hover:text-primary hover:bg-primary/10 transition-colors"
          >
            <span role="img" aria-label="Phone">📞</span> 
            <span>{t.footer.phone}</span>
          </a>
          <span className="flex items-center gap-2 min-h-[44px] px-4">
            <span role="img" aria-label="Location">📍</span> 
            <span>{t.footer.location}</span>
          </span>
        </nav>
        
        {/* Social Links - 48px touch targets */}
        <nav 
          className="flex justify-center gap-4 mb-5"
          aria-label="Social media links"
        >
          <a 
            href="https://www.facebook.com/lorenauraofficial"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit our Facebook page"
            className={`w-14 h-14 rounded-full border flex items-center justify-center transition-all hover:border-primary hover:bg-primary/10 hover:scale-105 ${
              isDark 
                ? 'border-slate-700 text-slate-300 hover:text-primary' 
                : 'border-[#f2bfd4] text-[#4b3a2a] hover:text-primary'
            }`}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M22 12a10 10 0 1 0-11.5 9.9v-7h-2.1V12h2.1V9.8c0-2.1 1.2-3.3 3.1-3.3.9 0 1.8.2 1.8.2v2h-1c-1 0-1.3.6-1.3 1.2V12h2.3l-.4 2.9h-1.9v7A10 10 0 0 0 22 12z" />
            </svg>
          </a>
          <a 
            href={`https://wa.me/+88${t.whatsapp.number}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat with us on WhatsApp"
            className={`w-14 h-14 rounded-full border flex items-center justify-center transition-all hover:border-green-500 hover:bg-green-500/10 hover:scale-105 ${
              isDark 
                ? 'border-slate-700 text-slate-300 hover:text-green-400' 
                : 'border-[#f2bfd4] text-[#4b3a2a] hover:text-green-600'
            }`}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2a10 10 0 0 0-8.7 14.9L2 22l5.3-1.4A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3.1.8.8-3-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.7-6c-.3-.1-1.6-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.7 0-.3-.1-1.2-.4-2.3-1.4-.8-.7-1.3-1.6-1.5-1.9-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.1.2-.3.3-.5.1-.2 0-.4 0-.6-.1-.1-.7-1.6-1-2.2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-1 1-1 2.4s1 2.8 1.1 3c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 2-1.3.2-.6.2-1.2.2-1.3 0-.1-.2-.2-.5-.3z" />
            </svg>
          </a>
        </nav>
        
        {/* Copyright */}
        <div className={`pt-5 border-t ${isDark ? 'border-slate-800' : 'border-[#f2bfd4]'}`}>
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-[#7b5a6a]'}`}>
            {t.footer.copyright}
          </p>
          <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-[#7b5a6a]'}`}>
            {t.footer.madeWith} <span role="img" aria-label="love">❤️</span> {t.footer.inBangladesh} <span role="img" aria-label="Bangladesh flag">🇧🇩</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
